import { ReadField } from 'components/ReadField';
import type { BimContractData } from 'gql/graphql';
import { BimLimitedCostTypeEnum } from 'gql/graphql';
import { Grid, GridItem, Tabs, Text } from 'uikit';
import { formatNumberToRu, isNotNil } from 'utils';

import { useCallback, useMemo, useState } from 'react';

import { limitedCostsInformers } from '../../BimContractEdit/components/BimContractEditLimitedCosts';
import {
    bimLimitedCostCalcMethods,
    fotEnabledLimitedCostTypes,
    getLimitedCostRateValueDecimalScale,
    typesLimitedCost,
} from '../../BimContractEdit/components/types';

import { sumInRoublesRenderer } from './BimContractReadCommonForm';

const percentRenderer = (value: { rateValue: any; type: BimLimitedCostTypeEnum }) =>
    value?.rateValue !== undefined && (
        <Text size="s">{`${formatNumberToRu(
            Number(value?.rateValue),
            getLimitedCostRateValueDecimalScale(value.type),
        )}%`}</Text>
    );

interface IBimContractReadLimitedCosts {
    data?: BimContractData | null;
}

export const regionalFactorRenderer = (value: any) =>
    isNotNil(value) ? <Text size="s">{formatNumberToRu(Number(value), 2)}</Text> : '—';

export const BimContractReadLimitedCosts = (props: IBimContractReadLimitedCosts) => {
    const { data } = props;
    const { limitedCosts, constructionTypes, limitedCostCalcMethod } = data || {};
    const constructionTabs = useMemo(
        () => (constructionTypes || [])?.map((c) => ({ id: c.id, label: c.name })),
        [constructionTypes],
    );

    const [activeTab, setActiveTab] = useState(constructionTabs?.[0]);

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const currentCost = useMemo(
        () => limitedCosts?.find((c) => c.constructionType.id === activeTab?.id),
        [activeTab?.id, limitedCosts],
    );

    const limitItems = useMemo(() => {
        const limitsTypes = currentCost?.items?.map((i) => i.type);

        return typesLimitedCost
            .filter((t) => limitsTypes?.includes(t.type))
            .map((t) => {
                const item = currentCost?.items?.find((i) => i.type === t.type);

                return {
                    label: t.label,
                    rateValue: item?.rateValue,
                    fotValue: item?.fotValue,
                    type: t.type,
                };
            });
    }, [currentCost?.items]);

    return (
        <Grid rowGap="xl" cols="1">
            <GridItem>
                <ReadField
                    value={bimLimitedCostCalcMethods.find((b) => b.id === limitedCostCalcMethod)?.label}
                    label="Учет МТР Заказчика при расчете лимитированных затрат"
                />
            </GridItem>
            <GridItem>
                <Tabs
                    getItemAs={() => 'div'}
                    value={activeTab}
                    onChange={onTabChange}
                    items={constructionTabs}
                    view="bordered"
                    size="s"
                />
            </GridItem>
            <GridItem>
                <ReadField
                    value={currentCost?.regionalFactor}
                    label="Районный коэффициент"
                    render={regionalFactorRenderer}
                />
            </GridItem>
            {limitItems.map((item, index) => (
                <GridItem key={index} style={{ display: 'flex' }}>
                    <ReadField
                        value={item}
                        label={`${item.label}, %`}
                        render={percentRenderer}
                        labelLeftSide={
                            item.type === BimLimitedCostTypeEnum.ShiftMethodOrganizationCost
                                ? limitedCostsInformers[0].value
                                : undefined
                        }
                    />
                    {fotEnabledLimitedCostTypes.includes(item.type) && (
                        <ReadField value={item.fotValue} label="ФОТ" render={sumInRoublesRenderer} />
                    )}
                </GridItem>
            ))}
        </Grid>
    );
};

BimContractReadLimitedCosts.displayName = 'BimContractReadLimitedCosts';
